/**
 * Registration / onboarding React Query hooks.
 *
 * Thin wrappers around `useApi()` covering the post-signup flows:
 *   - onboarding wizard state machine (['onboarding'])
 *   - company-type profile (['company-type-profile'])
 *   - EULA viewing and acceptance (['eula', ...])
 *
 * The public `/api/public/registration/*` endpoints used during signup
 * itself live in `./public.ts` because they must NOT carry a bearer token.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/hooks/useAppApi'
import type {
  OnboardingStateDto,
  OnboardingStepId,
  TenantCompanyTypeProfileDto,
  EulaVersionDto,
  EulaAcceptanceDto,
} from '@shared/types/registration'

// ---------------------------------------------------------------------------
// Onboarding
// ---------------------------------------------------------------------------

export function useOnboardingState() {
  const api = useApi()
  return useQuery<OnboardingStateDto>({
    queryKey: ['onboarding'],
    queryFn: () => api.get('/onboarding/state') as Promise<OnboardingStateDto>,
  })
}

export function useOnboardingMutations() {
  const api = useApi()
  const qc = useQueryClient()
  return {
    completeStep: useMutation({
      mutationFn: (step: OnboardingStepId) =>
        api.post('/onboarding/complete-step', { step }) as Promise<OnboardingStateDto>,
      onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding'] }),
    }),
  }
}

// ---------------------------------------------------------------------------
// Company-type profile
// ---------------------------------------------------------------------------

export function useCompanyTypeProfile() {
  const api = useApi()
  return useQuery<TenantCompanyTypeProfileDto>({
    queryKey: ['company-type-profile'],
    queryFn: () =>
      api.get('/tenants/me/company-type-profile') as Promise<TenantCompanyTypeProfileDto>,
  })
}

export function useCompanyTypeProfileMutations() {
  const api = useApi()
  const qc = useQueryClient()
  return {
    upsert: useMutation({
      mutationFn: (patch: Omit<TenantCompanyTypeProfileDto, 'tenantId'>) =>
        api.put('/tenants/me/company-type-profile', patch) as Promise<TenantCompanyTypeProfileDto>,
      onSuccess: () =>
        qc.invalidateQueries({ queryKey: ['company-type-profile'] }),
    }),
  }
}

// ---------------------------------------------------------------------------
// EULA
// ---------------------------------------------------------------------------

export function useCurrentEula(region: string) {
  const api = useApi()
  return useQuery<EulaVersionDto>({
    queryKey: ['eula', 'current', region],
    queryFn: () =>
      api.get(`/eula/current?region=${encodeURIComponent(region)}`) as Promise<EulaVersionDto>,
    enabled: !!region,
  })
}

export function useMyEulaAcceptances() {
  const api = useApi()
  return useQuery<EulaAcceptanceDto[]>({
    queryKey: ['eula', 'me'],
    queryFn: () => api.get('/eula/me') as Promise<EulaAcceptanceDto[]>,
  })
}

export function useEulaMutations() {
  const api = useApi()
  const qc = useQueryClient()
  return {
    accept: useMutation({
      mutationFn: (eulaVersionId: string) =>
        api.post('/eula/accept', { eulaVersionId }) as Promise<EulaAcceptanceDto>,
      onSuccess: () => qc.invalidateQueries({ queryKey: ['eula'] }),
    }),
  }
}
